import React from "react";
import { FileText, Download, CircleCheck, ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const resources = [{
  title: "Daily Cleaning Checklist",
  description: "A simple, stress-free guide to keeping your home tidy in just 15 minutes a day.",
  format: "PDF Guide",
  size: "1.2 MB"
}, {
  title: "The Minimalist Move-In Guide",
  description: "Essential steps for setting up a clutter-free home from day one in a new space.",
  format: "PDF Guide",
  size: "2.4 MB"
}, {
  title: "Pantry Organization Labels",
  description: "Printable minimalist labels for jars, bins, and baskets to unify your pantry look.",
  format: "Printable PDF",
  size: "0.8 MB"
}, {
  title: "Decluttering Roadmap",
  description: "A room-by-room plan to help you clear the clutter without feeling overwhelmed.",
  format: "Workbook",
  size: "3.1 MB"
}, {
  title: "Seasonal Wardrobe Planner",
  description: "Track your capsule wardrobe and plan seasonal switches with this easy template.",
  format: "Excel Template",
  size: "0.5 MB"
}, {
  title: "Kids' Chore Chart",
  description: "Age-appropriate chores and a fun tracking system to get the whole family involved.",
  format: "Printable PDF",
  size: "1.5 MB"
}];
export default function EssentialResources() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="essential-resources" ref={ref} className={`py-24 bg-background transition-opacity duration-700 ${isVisible ? "opacity-100" : "opacity-0"}`}>
      <div className="container mx-auto px-4">
        <div className="max-w-3xl mx-auto text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-serif font-medium text-foreground mb-4">
            Essential Resources
          </h2>
          <p className="text-muted-foreground text-lg">
            Curated tools, checklists, and guides designed to help you create a
            calm and functional living space.
          </p>
        </div>

        <div className="w-full">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {resources.map((resource, i) => <Card key={i} data-index={i} className="h-full flex flex-col border border-border/60 bg-card/50 hover:bg-card hover:border-border hover:shadow-md transition-all duration-300 group">
                <CardHeader className="pb-4">
                  <div className="flex justify-between items-start mb-4">
                    <div className="w-12 h-12 rounded-lg bg-secondary/40 flex items-center justify-center text-[var(--chart-1)] group-hover:bg-[var(--chart-1)]/10 transition-colors">
                      <FileText className="w-6 h-6" />
                    </div>
                    <div className="px-2.5 py-1 rounded-full bg-secondary/30 text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      {resource.format}
                    </div>
                  </div>
                  <CardTitle className="text-xl font-serif font-medium text-foreground group-hover:text-[var(--chart-1)] transition-colors">
                    {resource.title}
                  </CardTitle>
                </CardHeader>
                <CardContent className="flex-grow">
                  <CardDescription className="text-base text-muted-foreground leading-relaxed">
                    {resource.description}
                  </CardDescription>
                  <div className="mt-4 flex items-center gap-2 text-xs text-muted-foreground/70">
                    <CircleCheck className="w-3.5 h-3.5" />
                    <span>Instant Download • {resource.size}</span>
                  </div>
                </CardContent>
                <CardFooter className="pt-4">
                  <Button asChild className="w-full bg-[var(--chart-1)] hover:bg-[var(--chart-1)]/90 text-white shadow-sm hover:shadow transition-all cursor-pointer">
                    <Link to="#" aria-label={`Download ${resource.title}`}>
                      <Download className="w-4 h-4 mr-2" />
                      Download Now
                    </Link>
                  </Button>
                </CardFooter>
              </Card>)}
          </div>
        </div>

        <div className="mt-16 text-center">
          <Button asChild variant="ghost" className="text-muted-foreground hover:text-foreground hover:bg-transparent group">
            <Link to="/ToolkitPage">
              View all resources in The Toolkit
              <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}